import {
  defaultSystemCode,
  exists,
  SystemCode,
  SystemCodeFromJSON
} from '@/api'
import { dateTimeToPlainFormat, dateToPlainFormat } from '@/utils'

export interface Collection {
  id: number
  collectionNo: string
  parentCollectionId: number
  parentCollectionNo: string
  parentComposedCollectionId: number
  parentComposedCollectionNo: string
  childComposedCollectionIds: number[]
  parentBibliographyId: number
  parentDisplayTitle: string
  status: SystemCode
  removeReason: SystemCode
  removeTime: string
  removeExpectedDate: string
  collectionType: SystemCode
  handlingType: SystemCode
  storeType: SystemCode
  appendixCode: SystemCode
  opacShow: boolean
  processedTime: string
  processedBranchCode: SystemCode
  processedPlaceCode: SystemCode
  processedSystemUserId: number
  holdingBranchCode: SystemCode
  departmentCode: SystemCode
  forwardingBranchCode: SystemCode
  shelfType: SystemCode
  shelfTypeOld: SystemCode
  mark: string
  ndc: string
  book: string
  sub: string
  specialShelfLimitDate: string
  acceptDate: string
  acceptType: SystemCode
  acceptPrice: number
  listPrice: number
  applicantUserId: string
  traderCode: SystemCode
  donorCode: SystemCode
  budgetYear: number
  budgetBranchCode: SystemCode
  budgetDepartmentCode: SystemCode
  budgetExpenseCode: SystemCode
  equipType: SystemCode
  workMemo: string
  workMemoTime: string
  workMemoPlaceCode: SystemCode
  displayMemo: string
  displayMemoTime: string
  displayMemoPlaceCode: SystemCode
  localMemo: string
  localMemoTime: string
  localMemoPlaceCode: SystemCode
  checkedDate: string
  unknownCount: number
  collectionLendUserId: string
  collectionLendDate: string
  collectionLendType: SystemCode
  collectionLendBranchCode: SystemCode
  collectionLendPlaceCode: SystemCode
  dueDate: string
  interlibraryLoanCode: SystemCode
  borrowingDate: string
  borrowingDueDate: string
  remindCount: number
  remindDate: string
  lendTotalCount: number
  lendCount0: number
  lendCount1: number
  lendCount2: number
  lendCount3: number
  lendCount4: number
  lendCount5: number
  revision: number
  publishDate: string
  icTagUid: string
  setNumber: number
}

export const defaultCollection = (): Collection => ({
  id: 0,
  collectionNo: '',
  parentCollectionId: 0,
  parentCollectionNo: '',
  parentComposedCollectionId: 0,
  parentComposedCollectionNo: '',
  childComposedCollectionIds: [],
  parentBibliographyId: 0,
  parentDisplayTitle: '',
  status: defaultSystemCode(),
  removeReason: defaultSystemCode(),
  removeTime: '',
  removeExpectedDate: '',
  collectionType: defaultSystemCode(),
  handlingType: defaultSystemCode(),
  storeType: defaultSystemCode(),
  appendixCode: defaultSystemCode(),
  opacShow: false,
  processedTime: '',
  processedBranchCode: defaultSystemCode(),
  processedPlaceCode: defaultSystemCode(),
  processedSystemUserId: 0,
  holdingBranchCode: defaultSystemCode(),
  departmentCode: defaultSystemCode(),
  forwardingBranchCode: defaultSystemCode(),
  shelfType: defaultSystemCode(),
  shelfTypeOld: defaultSystemCode(),
  mark: '',
  ndc: '',
  book: '',
  sub: '',
  specialShelfLimitDate: '',
  acceptDate: '',
  acceptType: defaultSystemCode(),
  acceptPrice: 0,
  listPrice: 0,
  applicantUserId: '',
  traderCode: defaultSystemCode(),
  donorCode: defaultSystemCode(),
  budgetYear: 0,
  budgetBranchCode: defaultSystemCode(),
  budgetDepartmentCode: defaultSystemCode(),
  budgetExpenseCode: defaultSystemCode(),
  equipType: defaultSystemCode(),
  workMemo: '',
  workMemoTime: '',
  workMemoPlaceCode: defaultSystemCode(),
  displayMemo: '',
  displayMemoTime: '',
  displayMemoPlaceCode: defaultSystemCode(),
  localMemo: '',
  localMemoTime: '',
  localMemoPlaceCode: defaultSystemCode(),
  checkedDate: '',
  unknownCount: 0,
  collectionLendUserId: '',
  collectionLendDate: '',
  collectionLendType: defaultSystemCode(),
  collectionLendBranchCode: defaultSystemCode(),
  collectionLendPlaceCode: defaultSystemCode(),
  dueDate: '',
  interlibraryLoanCode: defaultSystemCode(),
  borrowingDate: '',
  borrowingDueDate: '',
  remindCount: 0,
  remindDate: '',
  lendTotalCount: 0,
  lendCount0: 0,
  lendCount1: 0,
  lendCount2: 0,
  lendCount3: 0,
  lendCount4: 0,
  lendCount5: 0,
  revision: 0,
  publishDate: '',
  icTagUid: '',
  setNumber: 0
})

export function CollectionFromJSON(json: any): Collection {
  return {
    id: exists(json, 'id') ? json.id : 0,
    collectionNo: exists(json, 'collection_no') ? json.collection_no : '',
    parentCollectionId: exists(json, 'parent_collection_id')
      ? json.parent_collection_id
      : 0,
    parentCollectionNo: exists(json, 'parent_collection_no')
      ? json.parent_collection_no
      : '',
    parentComposedCollectionId: exists(json, 'parent_composed_collection_id')
      ? json.parent_composed_collection_id
      : 0,
    parentComposedCollectionNo: exists(json, 'parent_composed_collection_no')
      ? json.parent_composed_collection_no
      : '',
    childComposedCollectionIds: exists(json, 'child_composed_collection_ids')
      ? json.child_composed_collection_ids
      : [],
    parentBibliographyId: exists(json, 'parent_bibliography_id')
      ? json.parent_bibliography_id
      : 0,
    parentDisplayTitle: exists(json, 'parent_display_title')
      ? json.parent_display_title
      : '',
    status: exists(json, 'status')
      ? SystemCodeFromJSON(json.status)
      : defaultSystemCode(),
    removeReason: exists(json, 'remove_reason')
      ? SystemCodeFromJSON(json.remove_reason)
      : defaultSystemCode(),
    removeTime: exists(json, 'remove_time')
      ? dateTimeToPlainFormat(json.remove_time)
      : '',
    removeExpectedDate: exists(json, 'remove_expected_date')
      ? dateToPlainFormat(json.remove_expected_date)
      : '',
    collectionType: exists(json, 'collection_type')
      ? SystemCodeFromJSON(json.collection_type)
      : defaultSystemCode(),
    handlingType: exists(json, 'handling_type')
      ? SystemCodeFromJSON(json.handling_type)
      : defaultSystemCode(),
    storeType: exists(json, 'store_type')
      ? SystemCodeFromJSON(json.store_type)
      : defaultSystemCode(),
    appendixCode: exists(json, 'appendix_code')
      ? SystemCodeFromJSON(json.appendix_code)
      : defaultSystemCode(),
    opacShow: exists(json, 'opac_show') ? json.opac_show : false,
    processedTime: exists(json, 'processed_time')
      ? dateTimeToPlainFormat(json.processed_time)
      : '',
    processedBranchCode: exists(json, 'processed_branch_code')
      ? SystemCodeFromJSON(json.processed_branch_code)
      : defaultSystemCode(),
    processedPlaceCode: exists(json, 'processed_place_code')
      ? SystemCodeFromJSON(json.processed_place_code)
      : defaultSystemCode(),
    processedSystemUserId: exists(json, 'processed_system_user_id')
      ? json.processed_system_user_id
      : 0,
    holdingBranchCode: exists(json, 'holding_branch_code')
      ? SystemCodeFromJSON(json.holding_branch_code)
      : defaultSystemCode(),
    departmentCode: exists(json, 'department_code')
      ? SystemCodeFromJSON(json.department_code)
      : defaultSystemCode(),
    forwardingBranchCode: exists(json, 'forwarding_branch_code')
      ? SystemCodeFromJSON(json.forwarding_branch_code)
      : defaultSystemCode(),
    shelfType: exists(json, 'shelf_type')
      ? SystemCodeFromJSON(json.shelf_type)
      : defaultSystemCode(),
    shelfTypeOld: exists(json, 'shelf_type_old')
      ? SystemCodeFromJSON(json.shelf_type_old)
      : defaultSystemCode(),
    mark: exists(json, 'mark') ? json.mark : '',
    ndc: exists(json, 'ndc') ? json.ndc : '',
    book: exists(json, 'book') ? json.book : '',
    sub: exists(json, 'sub') ? json.sub : '',
    specialShelfLimitDate: exists(json, 'special_shelf_limit_date')
      ? dateToPlainFormat(json.special_shelf_limit_date)
      : '',
    acceptDate: exists(json, 'accept_date')
      ? dateToPlainFormat(json.accept_date)
      : '',
    acceptType: exists(json, 'accept_type')
      ? SystemCodeFromJSON(json.accept_type)
      : defaultSystemCode(),
    acceptPrice: exists(json, 'accept_price') ? json.accept_price : 0,
    listPrice: exists(json, 'list_price') ? json.list_price : 0,
    applicantUserId: exists(json, 'applicant_user_id')
      ? json.applicant_user_id
      : '',
    traderCode: exists(json, 'trader_code')
      ? SystemCodeFromJSON(json.trader_code)
      : defaultSystemCode(),
    donorCode: exists(json, 'donor_code')
      ? SystemCodeFromJSON(json.donor_code)
      : defaultSystemCode(),
    budgetYear: exists(json, 'budget_year') ? json.budget_year : 0,
    budgetBranchCode: exists(json, 'budget_branch_code')
      ? SystemCodeFromJSON(json.budget_branch_code)
      : defaultSystemCode(),
    budgetDepartmentCode: exists(json, 'budget_department_code')
      ? SystemCodeFromJSON(json.budget_department_code)
      : defaultSystemCode(),
    budgetExpenseCode: exists(json, 'budget_expense_code')
      ? SystemCodeFromJSON(json.budget_expense_code)
      : defaultSystemCode(),
    equipType: exists(json, 'equip_type')
      ? SystemCodeFromJSON(json.equip_type)
      : defaultSystemCode(),
    workMemo: exists(json, 'work_memo') ? json.work_memo : '',
    workMemoTime: exists(json, 'work_memo_time')
      ? dateTimeToPlainFormat(json.work_memo_time)
      : '',
    workMemoPlaceCode: exists(json, 'work_memo_place_code')
      ? SystemCodeFromJSON(json.work_memo_place_code)
      : defaultSystemCode(),
    displayMemo: exists(json, 'display_memo') ? json.display_memo : '',
    displayMemoTime: exists(json, 'display_memo_time')
      ? dateTimeToPlainFormat(json.display_memo_time)
      : '',
    displayMemoPlaceCode: exists(json, 'display_memo_place_code')
      ? SystemCodeFromJSON(json.display_memo_place_code)
      : defaultSystemCode(),
    localMemo: exists(json, 'local_memo') ? json.local_memo : '',
    localMemoTime: exists(json, 'local_memo_time')
      ? dateTimeToPlainFormat(json.local_memo_time)
      : '',
    localMemoPlaceCode: exists(json, 'local_memo_place_code')
      ? SystemCodeFromJSON(json.local_memo_place_code)
      : defaultSystemCode(),
    checkedDate: exists(json, 'checked_date')
      ? dateToPlainFormat(json.checked_date)
      : '',
    unknownCount: exists(json, 'unknown_count') ? json.unknown_count : 0,
    collectionLendUserId: exists(json, 'collection_lend_user_id')
      ? json.collection_lend_user_id
      : '',
    collectionLendDate: exists(json, 'collection_lend_date')
      ? dateToPlainFormat(json.collection_lend_date)
      : '',
    collectionLendType: exists(json, 'collection_lend_type')
      ? SystemCodeFromJSON(json.collection_lend_type)
      : defaultSystemCode(),
    collectionLendBranchCode: exists(json, 'collection_lend_branch_code')
      ? SystemCodeFromJSON(json.collection_lend_branch_code)
      : defaultSystemCode(),
    collectionLendPlaceCode: exists(json, 'collection_lend_place_code')
      ? SystemCodeFromJSON(json.collection_lend_place_code)
      : defaultSystemCode(),
    dueDate: exists(json, 'due_date') ? dateToPlainFormat(json.due_date) : '',
    interlibraryLoanCode: exists(json, 'interlibrary_loan_code')
      ? SystemCodeFromJSON(json.interlibrary_loan_code)
      : defaultSystemCode(),
    borrowingDate: exists(json, 'borrowing_date')
      ? dateToPlainFormat(json.borrowing_date)
      : '',
    borrowingDueDate: exists(json, 'borrowing_due_date')
      ? dateToPlainFormat(json.borrowing_due_date)
      : '',
    remindCount: exists(json, 'remind_count') ? json.remind_count : 0,
    remindDate: exists(json, 'remind_date')
      ? dateToPlainFormat(json.remind_date)
      : '',
    lendTotalCount: exists(json, 'lend_total_count')
      ? json.lend_total_count
      : 0,
    lendCount0: exists(json, 'lend_count_0') ? json.lend_count_0 : 0,
    lendCount1: exists(json, 'lend_count_1') ? json.lend_count_1 : 0,
    lendCount2: exists(json, 'lend_count_2') ? json.lend_count_2 : 0,
    lendCount3: exists(json, 'lend_count_3') ? json.lend_count_3 : 0,
    lendCount4: exists(json, 'lend_count_4') ? json.lend_count_4 : 0,
    lendCount5: exists(json, 'lend_count_5') ? json.lend_count_5 : 0,
    revision: exists(json, 'revision') ? json.revision : 0,
    publishDate: exists(json, 'publish_date')
      ? dateToPlainFormat(json.publish_date)
      : '',
    icTagUid: exists(json, 'ic_tag_uid') ? json.ic_tag_uid : '',
    setNumber: exists(json, 'set_number') ? json.set_number : 0
  }
}
